import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionIcon, Button, Group, TextInput, Tooltip } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconStethoscope,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import {
  GetMedicalSpecialitiesRequestParams,
  MedicalSpecialitiesSortBy,
  MedicalSpeciality,
} from '@interfaces/medicalSpecialities.interface';

import { isApiError } from '@api/index';
import {
  useCountMedicalSpecialitiesQuery,
  useDeleteMedicalSpecialityMutation,
  useGetManyMedicalSpecialitiesQuery,
} from '@api/medicalSpecialities.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertMedicalSpecialityForm, {
  UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID,
} from '@components/medicalSpecialities/UpsertMedicalSpecialityForm';

export default function MedicalSpecialitiesList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateMedicalSpecialities = useUserPermissionGuard(
    'MEDICAL_SPECIALITIES.CREATE',
  );
  const canEditMedicalSpecialities = useUserPermissionGuard(
    'MEDICAL_SPECIALITIES.UPDATE',
  );
  const canDeleteMedicalSpecialities = useUserPermissionGuard(
    'MEDICAL_SPECIALITIES.DELETE',
  );

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetMedicalSpecialitiesRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<MedicalSpeciality>
  >({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as MedicalSpecialitiesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: medicalSpecialitiesCount = { count: 0 },
    isLoading: isLoadingCount,
  } = useCountMedicalSpecialitiesQuery({
    nameSearch: nameSearchDebounced,
  });

  const { data = [], isLoading } = useGetManyMedicalSpecialitiesQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
  });

  const [
    deleteMedicalSpeciality,
    { isLoading: isLoadingDeleteMedicalSpeciality },
  ] = useDeleteMedicalSpecialityMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddMedicalSpecialityClick = useCallback(() => {
    openModal({
      modalId: UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID,
      title: 'Aggiungi specialità medica',
      children: <UpsertMedicalSpecialityForm />,
    });
  }, []);

  const handleEditMedicalSpecialityClick = useCallback(
    (medicalSpeciality: MedicalSpeciality) => {
      openModal({
        modalId: UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID,
        title: 'Modifica specialità medica',
        children: (
          <UpsertMedicalSpecialityForm medicalSpeciality={medicalSpeciality} />
        ),
      });
    },
    [],
  );

  const handleDeleteMedicalSpecialityClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina specialità medica',
      children:
        "Stai per eliminare la specialità medica. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteMedicalSpeciality(id).unwrap();

          showNotification({
            title: 'Specialità medica eliminata',
            message: 'Specialità medica eliminata con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            if (e.data.code === 'P2003') {
              showNotification({
                title: 'Impossibile eliminare',
                message: 'La specialità medica è in uso da un medico',
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            }
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconStethoscope />
        Specialità mediche{' '}
        {!isLoadingCount ? `(${medicalSpecialitiesCount.count})` : ''}
      </Group>
    ),
    [medicalSpecialitiesCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateMedicalSpecialities ? (
        <Button
          leftSection={<IconPlus />}
          onClick={handleAddMedicalSpecialityClick}
        >
          Aggiungi specialità medica
        </Button>
      ) : (
        <></>
      ),
    [handleAddMedicalSpecialityClick, canCreateMedicalSpecialities],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessuna specialità medica trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {canEditMedicalSpecialities && (
                  <Tooltip label="Modifica specialità medica">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditMedicalSpecialityClick(record)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDeleteMedicalSpecialities && (
                  <Tooltip label="Elimina specialità medica">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isLoadingDeleteMedicalSpeciality}
                      onClick={() =>
                        handleDeleteMedicalSpecialityClick(record.id)
                      }
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={medicalSpecialitiesCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
